import React, { useCallback, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { Utils, fetchWithAuth, fetchAjaxListData, can, formatDateTime, exportListCsv } from '../../utils';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal, Collapse } from 'react-bootstrap';
import SkeletonLoader from "../common/SkeletonLoader";
import UsersFiltersBar from "./UsersFiltersBar";
import { IoIosLink } from "react-icons/io";
import { FiRefreshCw } from "react-icons/fi";
import { MdDeleteOutline, MdKeyboardArrowDown, MdAddCircle, MdFileDownload } from "react-icons/md";
import { IoEye } from 'react-icons/io5';
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";
import { EMPTY_USERS_LIST_FILTERS, buildUsersListBody, filtersObjectHasAnyValue, getDefaultPageSize, getTotalPages, getBrowserTimeZone } from "../../utils";
import Swal from "sweetalert2";

const Users = () => {

    const { appId } = useParams()
    const [appUsers, setAppUsers] = useState([])
    const tableScrollRef = useRef(null);
    const tableInnerRef = useRef(null);
    const navigate = useNavigate();

    const {
        openActionItem: openItem,
        dropdownPosition,
        dropdownRef,
        closeDropdown,
        handleDropdownAction,
        handleOpenDropdown,
    } = useActionDropdown({
      tableScrollRef,
      optionCount: () => 2 + (can("app_users.delete") ? 1 : 0),
      menuWidth: 200,
    });

    const tableMaxHeight = useTableMaxHeight(tableScrollRef);
    const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, appUsers?.length);

    const [noDataFound, setNoDataFound] = useState(false) // eslint-disable-line no-unused-vars
    const [isLoading, setIsLoading] = useState(true)
    const [csvFile, setCsvFile] = useState(null);
    const [showCsvModal, setShowCsvModal] = useState(false);
    const [parsedCsvData, setParsedCsvData] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const pageSize = getDefaultPageSize();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [draftFilters, setDraftFilters] = useState(() => ({ ...EMPTY_USERS_LIST_FILTERS }));
    const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_USERS_LIST_FILTERS }));
    const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);

    useEffect(() => {
        const hasContent =
            filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
        if (!hasContent) {
            setFilterAccordionOpen(false);
            return;
        }
        setFilterAccordionOpen(true);
    }, [draftFilters, appliedFilters]);

    const totalPages = getTotalPages(totalRows, pageSize);

    const handleOpenModal = (id) => {
        closeDropdown();
        navigate(`/apps/${appId}/subscriptions?notify_sphere_id=${id}`);
    };

    const handleCsvUpload = (event) => {
        const file = event.target.files[0];
        if (file && file.type === 'text/csv') {
            setCsvFile(file);
            parseCsv(file);
        } else {
            toast.warning('Please upload a valid CSV file');
        }
    };

    const parseCsv = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split('\n');
            const headers = lines[0].split(',').map(header => header.trim());

            // Required CSV fields validation
            const requiredFields = [
                'timezone_id',
                'phone_number',
                'app_version',
                'country',
                'device',
                'channel',
                'player_id',
                'language_code',
                'lat',
                'lon',
                'push_token',
                'rooted',
                'sdk_version',
                'subscribed',
                'tags',
                'onesignal_id'
            ];

            // Check for missing required fields
            const missingFields = requiredFields.filter(field => !headers.includes(field));

            if (missingFields.length > 0) {
                toast.error(`CSV validation failed! Missing required fields: ${missingFields.join(', ')}`);
                setCsvFile(null);
                setParsedCsvData([]);
                console.error('Missing CSV fields:', missingFields);
                return;
            }

            const csvData = [];

            for (let i = 1; i < lines.length; i++) {
                if (lines[i].trim()) {
                    const values = lines[i].split(',').map(value => value.trim());
                    const row = {};
                    headers.forEach((header, index) => {
                        row[header] = values[index];
                    });
                    csvData.push(row);
                }
            }

            // Transform CSV data for database insertion
            const transformedData = csvData.map((row) => {
                // Parse device field to extract model and version
                const deviceParts = row.device ? row.device.split(' ') : [];
                const deviceModel = deviceParts.slice(0, -1).join(' ') || row.device;
                const deviceVersion = deviceParts[deviceParts.length - 1] || '';

                return {
                    // Data for subscriptions table
                    subscription: {
                        subscriptionId: row.player_id,
                        device: deviceModel,
                        version: row.app_version,
                        device_os: row.channel,
                        token: row.push_token,
                        applicationId: appId, // Dynamic from URL params
                        appUserId: null, // Will be set after app_user is created
                        language: row.language_code,
                        country: row.country,
                        timezone: row.timezone_id,
                        sdk_version: row.sdk_version,
                        rooted: row.rooted === 'true',
                        subscribed: row.subscribed === 'true',
                        unsubscribed_at: row.unsubscribed_at || null
                    },
                    // Data for app_users table
                    app_user: {
                        notify_sphere_id: row.onesignal_id,
                        lat: row.lat,
                        long: row.lon,
                        tags: row.tags,
                        applicationId: appId, // Dynamic from URL params
                        mobile: row.phone_number,
                        device_name: row.device,
                        android_version: deviceVersion,
                        token: row.push_token
                    },
                    // Original CSV row for reference
                    original: row
                };
            });

            // Store parsed data in state
            setParsedCsvData(transformedData);
        };
        reader.readAsText(file);
    };

    const handleSaveCsvData = async () => {
        if (parsedCsvData.length === 0) {
            toast.warning('No data to save. Please upload a CSV file first.');
            return;
        }

        setIsSaving(true);

        try {
            // Call API to save CSV data
            const response = await fetchWithAuth(Utils.baseurl + 'import/csv/users', {
                method: 'POST',
                body: JSON.stringify({
                    applicationId: appId,
                    users: parsedCsvData.slice(0, 20) // Limit to first 20 records for this example
                }),
            });

            const result = await response.json();

            if (result.status) {
                toast.success(`Successfully imported ${parsedCsvData.length} users!`);

                // Refresh the user list
                getAppUser();

                // Close modal and reset
                handleCloseCsvModal();
                setParsedCsvData([]);
            } else {
                console.error('Failed to save CSV data:', result);
                toast.error('Failed to import users: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error saving CSV data:', error);
            toast.error('Error importing users: ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenCsvModal = () => {
        setShowCsvModal(true);
    };

    const handleCloseCsvModal = () => {
        setShowCsvModal(false);
        setCsvFile(null);
        setParsedCsvData([]);
    };


    function getAppUser(page = currentPage) {
        fetchWithAuth(Utils.baseurl + 'get/app/users', {
            method: 'POST',
            body: JSON.stringify({
                id: appId,
                page,
                pageSize,
                ...buildUsersListBody(appliedFilters),
                timezone: getBrowserTimeZone() || undefined,
            }),
        })
            .then((response) => response.json())
            .then(async (result) => {

                setIsLoading(false);
                if (result.status) {
                    setAppUsers(result.data.appUsersData)
                    setTotalRows(result.data?.meta?.total !== null && result.data?.meta?.total !== undefined ? Number(result.data.meta.total) : (result.data.appUsersData?.length || 0));
                    if (!result.data.appUsersData.length) {
                        setNoDataFound(true)
                    } else {
                        setNoDataFound(false)
                    }
                }

            })
            .catch((error) => {
                console.error(error);
                setIsLoading(false);
            });
    }


    useLayoutEffect(() => {
        setDraftFilters({ ...EMPTY_USERS_LIST_FILTERS });
        setAppliedFilters({ ...EMPTY_USERS_LIST_FILTERS });
        setCurrentPage(1);
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        setIsLoading(true);
        getAppUser(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, appliedFilters, appId]);

    const handleApplyFilters = () => {
        setAppliedFilters({ ...draftFilters });
        setCurrentPage(1);
    };

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const { status, message } = await exportListCsv('get/app/users', {
                    id: appId,
                    ...buildUsersListBody(appliedFilters),
                    timezone: getBrowserTimeZone() || undefined,
                }, "user-records.csv");
            if (!status) {
                toast.error(message);
                return;
            }
            if (message) toast.info(message);
        } catch (error) {
            console.error(error);
            toast.error('Export failed');
        } finally {
            setIsExporting(false);
        }
    };

    const handleResetFilters = () => {
        const cleared = { ...EMPTY_USERS_LIST_FILTERS };
        setDraftFilters(cleared);
        setAppliedFilters(cleared);
        setCurrentPage(1);
    };

    const fetchTagKeyOptions = useCallback(
        ({ search, ids }) =>
            fetchAjaxListData({
                type: "app-user-tag-keys",
                q: search,
                id: ids,
                extra: { app_id: appId },
                mode: "options",
            }),
        [appId]
    );

    async function handleAction(type, id) {

        if (type === "Delete") {
            closeDropdown();

            const confirmDelete = await Swal.fire({
                title: "Are you sure?",
                text: "Deleting this user will also delete all connected subscriptions.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, delete",
            });

            if (!confirmDelete.isConfirmed) {
                return;
            }

            // Proceed with deletion
            fetchWithAuth(Utils.baseurl + 'delete/app/users', {
                method: 'POST',
                body: JSON.stringify({ id: id }),
            })
                .then((response) => response.json())
                .then(async (result) => {
                    if (result.status) {
                        getAppUser()
                    }
                })
                .catch((error) => console.error(error));
        }

        if (type === "addtest") {
            closeDropdown();
            // navigate(`/apps/${id}`)
        }

    }


    let loaderData = [
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
    ]

    return (
        <div className="dashboard_right">
            <div className="dashboard_inner">
                <div className="dashboard_right_top">
                    <div className="dashboard_heading">
                        <h1>User Records</h1>
                    </div>
                    <div className="add_app" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => { setIsLoading(true); getAppUser(currentPage); }}>
                            <FiRefreshCw />
                            <span>Refresh</span>
                        </button>
                        <button
                            type="button"
                            className="add_app_btn_secondary btn_theme_secondary"
                            title="Export CSV for applied filters"
                            onClick={handleExport}
                            disabled={isExporting}
                        >
                            <MdFileDownload />
                            <span>{isExporting ? 'Exporting…' : 'Export'}</span>
                        </button>
                        <button
                            type="button"
                            className="add_app_btn btn_theme_primary"
                            title="Upload CSV"
                            onClick={handleOpenCsvModal}
                        >
                            <MdAddCircle />
                            <span>Upload CSV</span>
                        </button>
                    </div>
                </div>
                <div className="list-page-filter-card">
                    <button
                        type="button"
                        className="list-page-filter-accordion-header"
                        onClick={() => setFilterAccordionOpen((o) => !o)}
                        aria-expanded={filterAccordionOpen}
                        aria-controls="users-list-filter-panel"
                        id="users-list-filter-accordion"
                    >
                        <span className="list-page-filter-accordion-title">Filter</span>
                        <MdKeyboardArrowDown
                            className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? 'list-page-filter-accordion-chevron--open' : ''}`}
                            size={22}
                            aria-hidden
                        />
                    </button>
                    <Collapse in={filterAccordionOpen}>
                        <div
                            className="list-page-filter-accordion-body"
                            id="users-list-filter-panel"
                            role="region"
                            aria-labelledby="users-list-filter-accordion"
                        >
                            <UsersFiltersBar
                                draftFilters={draftFilters}
                                setDraftFilters={setDraftFilters}
                                onSearch={handleApplyFilters}
                                onReset={handleResetFilters}
                                fetchTagKeyOptions={fetchTagKeyOptions}
                                tagKeySelectEnabled={Boolean(appId)}
                            />
                        </div>
                    </Collapse>
                </div>
                <div className="dashboard_app_table dashboard_app_table--list-records">
                    <div className="dashboard_table">
                        <div
                            ref={tableScrollRef}
                            className="table_scroll_wrapper"
                        >
                            <div
                                ref={tableInnerRef}
                                className={`table_inner table_y_scroll ${isAtScrollEnd ? 'at-scroll-end' : ''}`}
                                style={{
                                    maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : 'calc(100vh - 320px)',
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                }}
                            >
                                <table className="table_outer table-sm">
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: "center" }}>Notify Sphere Id</th>
                                            <th style={{ textAlign: "center" }}>First Session</th>
                                            <th style={{ textAlign: "center" }}>Last Session</th>
                                            <th style={{ textAlign: "center" }}>Created</th>
                                            <th style={{ textAlign: "center" }}>Email</th>
                                            <th style={{ textAlign: "center" }}>Phone</th>
                                            <th style={{ textAlign: "center" }}>Latitude</th>
                                            <th style={{ textAlign: "center" }}>Longitude</th>
                                            <th style={{ textAlign: "center" }}>Tags</th>
                                            <th className="col-action" style={{ textAlign: "center" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appUsers.length === 0 ? (
                                            <tr>
                                                <td colSpan="10" style={{ textAlign: "center" }}>
                                                    {
                                                        isLoading ?
                                                            <SkeletonLoader columns={loaderData} rows={2} />
                                                            // <FontAwesomeIcon icon={faSpinner} style={{ marginTop: "10px" }} spin size="2x" />
                                                            : <p  >No data found</p>
                                                    }
                                                </td>
                                            </tr>
                                        ) :
                                            appUsers.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td style={{ textAlign: "center" }}><span>{item.notify_sphere_id}</span> </td>
                                                    <td style={{ textAlign: "center" }}><span>{formatDateTime(item.first_session || item.createdAt) || '—'}</span> </td>
                                                    <td style={{ textAlign: "center" }}><span>{formatDateTime(item.last_session || item.updatedAt) || '—'}</span> </td>
                                                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                        <span>{formatDateTime(item.createdAt) || '—'}</span>
                                                    </td>
                                                    <td style={{ textAlign: "center" }}><span>{item.email}</span> </td>
                                                    <td style={{ textAlign: "center" }}><span>{item.phone}</span> </td>
                                                    <td style={{ textAlign: "center" }}><span>{item.lat}</span> </td>
                                                    <td style={{ textAlign: "center" }}><span>{item.long}</span></td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '6px', justifyContent: 'center' }}>
                                                            {item.tags && (() => {
                                                                try {
                                                                    const tagsObj = typeof item.tags === 'string' ? JSON.parse(item.tags) : item.tags;
                                                                    return Object.entries(tagsObj).map(([key, value], idx) => (
                                                                        <span
                                                                            key={idx}
                                                                            style={{
                                                                                background: '#dfe3e8',
                                                                                color: '#4d5867ff',
                                                                                padding: '5px 12px',
                                                                                borderRadius: '4px',
                                                                                fontSize: '13px',
                                                                                fontWeight: '400',
                                                                                whiteSpace: 'nowrap',
                                                                                display: 'inline-block'
                                                                            }}
                                                                        >
                                                                            <strong>{key}</strong>: {value}
                                                                        </span>
                                                                    ));
                                                                } catch (e) {
                                                                    return <span>{item.tags}</span>;
                                                                }
                                                            })()}
                                                        </div>
                                                    </td>
                                                    <td className="col-action" style={{ textAlign: "center" }}>
                                                        <button type="button" className="dropdown-trigger-btn" onClick={(e) => handleOpenDropdown(e, item.id, item)}>
                                                            <svg viewBox="0 0 16 16" fill="currentColor">
                                                                <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" fill="currentColor">
                                                                </path>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            </div>
                            <ListPaginationBar
                                page={currentPage}
                                setPage={setCurrentPage}
                                total={totalRows}
                                pageSize={pageSize}
                                totalPages={totalPages}
                                ariaLabel="Users pagination"
                                loading={isLoading}
                            />

                            {/* CSV Upload Modal */}
                            <Modal show={showCsvModal} onHide={handleCloseCsvModal}>
                                <Modal.Header closeButton>
                                    <Modal.Title>Upload CSV File</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <div style={{ marginBottom: '15px' }}>
                                        <span style={{ fontSize: "15px", display: 'block', marginBottom: '10px' }}>
                                            Select a CSV file to upload:
                                        </span>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={handleCsvUpload}
                                            className="form-control"
                                        />
                                    </div>
                                    {csvFile && (
                                        <div style={{ marginTop: '10px', padding: '10px', background: '#f0f0f0', borderRadius: '4px' }}>
                                            <strong>Selected file:</strong> {csvFile.name}
                                            <br />
                                            <small style={{ color: '#666' }}>
                                                {parsedCsvData.length > 0
                                                    ? `${parsedCsvData.length} records ready to import`
                                                    : 'Check console for parsed data'}
                                            </small>
                                        </div>
                                    )}
                                </Modal.Body>
                                <Modal.Footer>
                                    <button
                                        type="button"
                                        className="btn_theme_secondary"
                                        onClick={handleCloseCsvModal}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </button>
                                    {parsedCsvData.length > 0 && (
                                        <button
                                            type="button"
                                            className="add_app_btn btn_theme_primary"
                                            onClick={handleSaveCsvData}
                                            disabled={isSaving}
                                        >
                                            <span>{isSaving ? 'Importing...' : `Import ${parsedCsvData.length} Users`}</span>
                                        </button>
                                    )}
                                </Modal.Footer>
                            </Modal>
                        </div>
                    </div>
                </div>
            </div>
            {openItem && dropdownPosition && createPortal(
                <div ref={dropdownRef} style={{ position: 'fixed', left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 10000, minWidth: '220px' }}>
                    <div className="dropdown-menu-subscription">
                        <button onClick={() => handleDropdownAction(() => handleOpenModal(openItem.notify_sphere_id))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
                            <IoIosLink size={20} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Go to Linked Subscriptions</span>
                        </button>
                        <button onClick={() => handleDropdownAction(() => navigate(`/apps/${appId}/users/${openItem.id}`))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
                            <IoEye size={20} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>View User Profile</span>
                        </button>
                        {can("app_users.delete") ? (
                        <button className="delete-class" onClick={() => handleDropdownAction(() => handleAction('Delete', openItem.id))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
                            <MdDeleteOutline size={20} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Delete User</span>
                        </button>
                        ) : null}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Users;
